import { createAsyncThunk } from '@reduxjs/toolkit';

import { conversationEndpoints } from '../../api/endpoints/chat/conversation-endpoints';
import { messageEndpoints } from '../../api/endpoints/chat/message-endpoints';
import { ISendMessageParams } from '../../api/types';
import { CHAT_PREFETCH_INTERVAL } from '../../components/Chat/ConversationsContext';
import { MixpanelEvent } from '../../services/mixpanel/types';
import { getUserProject } from '../../utils/user';
import { AppThunk, IRootState } from '../core-store';
import {
    _closeHistory,
    _openHistory,
    IChatTabSliceState,
    setActiveConversationId as _setActiveConversationId,
} from '../slices/chat-tab/chat-tab-slice';
import { setGrowSidebarWidthWithAnimation, setMinimizeSidebarWidthWithAnimation } from '../slices/resize/resize-slice';

import { sendMetrics } from './metrics-thunk';

import theme from '../../styles/chakra/theme.module.scss';

const chatHistoryMaxWidth = parseInt(theme.chatHistoryMaxWidth, 10);
const chatMinWidthWHistory = parseInt(theme.chatMinWidthWHistory, 10);

type TriggeredBy = 'user';

export const openHistory =
    (triggeredBy?: TriggeredBy): AppThunk =>
    (dispatch, getState) => {
        const sidebarWidth = getState().resize.sidebarWidth;
        let width = Math.max(chatMinWidthWHistory, chatHistoryMaxWidth + sidebarWidth);
        const windowWidth = window.innerWidth;

        if (width > windowWidth) {
            width = windowWidth;
        }

        dispatch(setGrowSidebarWidthWithAnimation(width));
        dispatch(_openHistory());

        if (triggeredBy === 'user') {
            dispatch(
                sendMetrics({
                    event: MixpanelEvent.CHAT_TAB_HISTORY_OPEN_CLICK,
                })
            );
        }
    };

export const closeHistory =
    (triggeredBy?: TriggeredBy): AppThunk =>
    (dispatch) => {
        dispatch(setMinimizeSidebarWidthWithAnimation());
        dispatch(_closeHistory());

        if (triggeredBy === 'user') {
            dispatch(
                sendMetrics({
                    event: MixpanelEvent.CHAT_TAB_HISTORY_CLOSE_CLICK,
                })
            );
        }
    };

type SetActiveConversationIdOptions = {
    triggeredBy?: TriggeredBy;
};
export const setActiveConversationId =
    (conversationId: IChatTabSliceState['activeConversationId'], options?: SetActiveConversationIdOptions): AppThunk =>
    (dispatch) => {
        dispatch(_setActiveConversationId(conversationId));
        if (options?.triggeredBy === 'user' && conversationId) {
            dispatch(
                sendMetrics({
                    event: MixpanelEvent.CHAT_TAB_HISTORY_CONVERSATION_CLICK,
                    meta: {
                        conversation_id: conversationId,
                    },
                })
            );
        }
    };

type Params = {
    customer_project_id: string;
    ttl?: number;
};

/**
 * Use to force prefetch all conversations and last conversation
 *
 * */
export const forcePrefetchChat = createAsyncThunk(
    'chat/forcePrefetch',
    async ({ customer_project_id, ttl }: Params, { getState, dispatch }) => {
        const state = getState() as IRootState;

        try {
            const initiateParams = ttl !== undefined ? { ifOlderThan: ttl } : { forceRefetch: true };
            const conversations = await dispatch(
                conversationEndpoints.endpoints.getAllConversations.initiate({ customer_project_id }, initiateParams)
            ).unwrap();

            let conversation = conversations[0];

            if (!conversation) {
                conversation = await dispatch(
                    conversationEndpoints.endpoints.createConversation.initiate({
                        customer_project_id,
                        action_id: undefined,
                        apps_version: undefined,
                    })
                ).unwrap();
            }

            const conversationId = state.chatTab.activeConversationId ?? conversation.conversation_id;

            await Promise.all([
                dispatch(
                    conversationEndpoints.endpoints.getConversationById.initiate(
                        { conversation_id: conversationId, customer_project_id },
                        initiateParams
                    )
                ),
                dispatch(
                    messageEndpoints.endpoints.getMessages.initiate(
                        { conversation_id: conversationId, customer_project_id },
                        initiateParams
                    )
                ),
            ]);

            dispatch(_setActiveConversationId(conversationId));
        } catch (e) {
            console.error('Failed to prefetch chat');
        }
    }
);

export const prefetchConversation =
    (conversationId: string): AppThunk =>
    (dispatch, getState) => {
        const state = getState();
        const projectId = getUserProject(state);

        dispatch(
            conversationEndpoints.util.prefetch(
                'getConversationById',
                {
                    conversation_id: conversationId,
                    customer_project_id: projectId,
                },
                {
                    ifOlderThan: CHAT_PREFETCH_INTERVAL,
                }
            )
        );
    };

/**
 * This is a workaround for triggering send message for the "boost" mode
 * it is here to avoid of depending on the "question" value from the state
 */
export const _triggerMessage = createAsyncThunk(
    'chat/trigger-send-message',
    async (params: Omit<ISendMessageParams, 'content'> & { content?: string }, { getState, dispatch }) => {
        const {
            question: { question },
        } = getState() as IRootState;

        const content = params.content ?? question;

        return dispatch(messageEndpoints.endpoints.sendMessage.initiate({ ...params, content }));
    }
);
